import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronLeft, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi } from '@/services/api';
import { useToast } from '@/components/ui/toast';

export function RegisterPage() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    passwordConfirm: '',
    email: '',
    displayName: '',
    eflightUsername: '',
    eflightPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  const { addToast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      addToast({ title: '请填写必填项', variant: 'warning' });
      return;
    }

    if (!formData.email) {
      addToast({ title: '请填写邮箱地址', variant: 'warning' });
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      addToast({ title: '两次密码输入不一致', variant: 'warning' });
      return;
    }

    setIsLoading(true);

    try {
      await authApi.register({
        username: formData.username,
        password: formData.password,
        passwordConfirm: formData.passwordConfirm,
        email: formData.email,
        displayName: formData.displayName,
        eflightUsername: formData.eflightUsername,
        eflightPassword: formData.eflightPassword,
      });
      setRegistrationComplete(true);
    } catch (error: any) {
      const data = error.response?.data;
      let message = '注册失败，请稍后重试';
      if (data?.message) {
        message = data.message;
      } else if (data && typeof data === 'object') {
        // DRF 字段级验证错误：{ password: ["太短"], username: ["已存在"] }
        const first = Object.values(data)[0];
        if (Array.isArray(first) && first.length > 0) message = String(first[0]);
      }
      addToast({ title: '注册失败', description: message, variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  if (registrationComplete) {
    return (
      <div className="text-center space-y-4 py-4">
        <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
          <Mail className="w-8 h-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">请验证您的邮箱</h3>
        <p className="text-gray-600 text-sm">
          验证邮件已发送至 <span className="font-medium">{formData.email}</span>，
          请查收并点击邮件中的链接完成注册。
        </p>
        <p className="text-xs text-gray-400">没有收到？请检查垃圾邮件文件夹。</p>
        <Link to="/login" className="block text-blue-600 hover:text-blue-700 text-sm font-medium pt-2">
          返回登录
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Link to="/login" className="text-gray-500 hover:text-gray-700">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <h2 className="text-xl font-semibold text-gray-900">注册账号</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="username">用户名 *</Label>
          <Input
            id="username"
            name="username"
            type="text"
            placeholder="请输入用户名"
            value={formData.username}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="password">密码 *</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="请输入密码"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="passwordConfirm">确认密码 *</Label>
            <Input
              id="passwordConfirm"
              name="passwordConfirm"
              type={showPassword ? 'text' : 'password'}
              placeholder="请再次输入密码"
              value={formData.passwordConfirm}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="showPassword"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
            className="rounded border-gray-300"
          />
          <Label htmlFor="showPassword" className="text-sm font-normal cursor-pointer">
            显示密码
          </Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="displayName">显示名称</Label>
          <Input
            id="displayName"
            name="displayName"
            type="text"
            placeholder="请输入显示名称"
            value={formData.displayName}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">邮箱 *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="请输入邮箱（用于验证）"
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>

        <div className="border-t pt-4 mt-4">
          <p className="text-sm text-gray-500 mb-4">eFlight 账号信息（可选，可在设置中配置）</p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eflightUsername">eFlight 用户名</Label>
              <Input
                id="eflightUsername"
                name="eflightUsername"
                type="text"
                placeholder="请输入 eFlight 用户名"
                value={formData.eflightUsername}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eflightPassword">eFlight 密码</Label>
              <Input
                id="eflightPassword"
                name="eflightPassword"
                type="password"
                placeholder="请输入 eFlight 密码"
                value={formData.eflightPassword}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              注册中...
            </>
          ) : (
            '注册'
          )}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600 mt-6">
        已有账号？{' '}
        <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
          立即登录
        </Link>
      </p>
    </div>
  );
}
